import { ReactElement, ReactNode, RefObject, useEffect, useRef } from 'react'
import ModalHeader from 'ui/molecules/ModalHeader'
import ModalFooter from 'ui/molecules/ModalFooter'
import { noOperations } from 'helpers'
import {
  HeaderOverrideWrapper,
  ModalContent,
  SlideInModal
} from './subcomponents'
import { ModalFooterProps } from '../ModalFooter/typing'

export interface ModalDialogFormProps {
  modalId: string
  modalTitle?: ReactElement | string
  isOpen: boolean
  onCloseModal?: () => void
  children?: ReactNode
  onSubmit?: () => void
  submitText?: string
  submitIcon?: ReactElement
  isSubmitEnabled?: boolean
  onCancel?: () => void
  cancelText?: string
  headerOverride?: ReactElement
  footerOverride?: ReactElement
  cancelStyles?: ModalFooterProps['secondaryStyles']
  submitStyles?: ModalFooterProps['primaryStyles']
  className?: string
  /** Reference to the modal footer primary button */
  primaryButtonRef?: RefObject<HTMLButtonElement>
  showOnlySecondaryButton?: ModalFooterProps['showOnlySecondaryButton']
  ['data-test']?: string
}

const ModalDialogForm = (props: ModalDialogFormProps): ReactElement => {
  const headerRef = useRef(null)

  const {
    modalId,
    modalTitle = '',
    isOpen,
    onCloseModal = noOperations,
    children = null,
    onSubmit = noOperations,
    submitText = 'Submit',
    submitIcon = null,
    isSubmitEnabled = true,
    submitStyles = {},
    onCancel = onCloseModal,
    cancelText = 'Cancel',
    cancelStyles = {},
    headerOverride = null,
    footerOverride = null,
    showOnlySecondaryButton = false,
    className,
    primaryButtonRef
  } = props

  useEffect(() => {
    return () => {
      // After the modal unmounts under any circumstances (E.g: x icon, close button, system error, etc), we set the overflow to 'auto' again so we can scroll the page.
      document.body.style.overflow = 'auto'
    }
  }, [])

  return (
    <SlideInModal
      id={`${modalId}Modal`}
      role="dialog"
      aria-modal
      aria-label={typeof modalTitle === 'string' ? modalTitle : ''}
      headerRef={headerRef}
      isOpen={isOpen}
      aria-hidden={!isOpen}
      aria-describedby={`${modalId}ModalDescription`}
      // eslint-disable-next-line
      data-test={props['data-test']}
      className={className}
      header={
        (headerOverride && (
          <HeaderOverrideWrapper ref={headerRef}>
            {headerOverride}
          </HeaderOverrideWrapper>
        )) || (
          <ModalHeader
            headerRef={headerRef}
            headerText={modalTitle}
            closeButton={onCloseModal}
          />
        )
      }
      footer={
        <div>
          {footerOverride || (
            <ModalFooter
              primaryButtonRef={primaryButtonRef}
              primaryButtonText={submitText}
              primaryButtonIcon={submitIcon}
              onPrimaryButtonClick={onSubmit}
              isPrimaryEnabled={isSubmitEnabled}
              secondaryButtonText={cancelText}
              onSecondaryButtonClick={onCancel}
              primaryStyles={submitStyles}
              secondaryStyles={cancelStyles}
              showOnlySecondaryButton={showOnlySecondaryButton}
            />
          )}
        </div>
      }
    >
      <ModalContent id={`${modalId}ModalDescription`}>{children}</ModalContent>
    </SlideInModal>
  )
}

export default ModalDialogForm
